import { createApi } from "@reduxjs/toolkit/query/react";
import customBaseQuery from "../../../utils/customBaseQuery";
import { gql } from "graphql-request";
import { BulkQuestionInput } from "../types/bulkActionOnQuestions";

export const McqAssessmentSliceApi = createApi({
  reducerPath: "mcqAssessmentApi",
  baseQuery: customBaseQuery,
  //   keepUnusedDataFor: 60,
  //   tagTypes: ["attendanceApi"],
  endpoints: (builder) => ({
    getMcqAssessmentApi: builder.query({
      query: ({ questionId, version, status }) => ({
        url: `/mcq/${questionId}/${version}/${status}`,
        method: "GET"
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    addMcqSubtypeAssessmentApi: builder.mutation({
      query: ({ subtype, access, data }: any) => ({
        url: `/mcq/${subtype}/${access}`,
        method: "POST",
        body: data
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    updateMcqAssessmentApi: builder.mutation({
      query: ({ questionId, version, data }: any) => ({
        url: `/mcq/${questionId}/${version}`,
        method: "PUT",
        body: data
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    updateMcqStatusApi: builder.mutation({
      query: ({ questionId, version, data }: any) => ({
        url: `/mcq/status/${questionId}/${version}`,
        method: "PUT",
        body: data
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    updateQuestionReviewRequiredApi: builder.mutation({
      query: ({ questionid, reviewrequired }: any) => {
        return {
          url: `/question/${questionid}`, // Path parameter
          params: { reviewrequired: reviewrequired },
          method: "PUT"
        };
      },
      extraOptions: { service: "MCQ_Assessment" }
    }),
    addCodingQn: builder.mutation({
      query: ({ access, data }: any) => ({
        url: `/codingQn/${access}`,
        method: "POST",
        body: data
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    updateCodingQn: builder.mutation({
      query: ({ questionId, version, data }: any) => ({
        url: `/codingQn/${questionId}/${version}`,
        method: "PUT",
        body: data
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    addTestCases: builder.mutation({
      query: ({ version, questionId, data }: any) => ({
        url: `/testcase/${questionId}/${version}`,
        method: "POST",
        body: data
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    getTestCases: builder.query({
      query: ({ version, questionId, data }: any) => ({
        url: `/testcase/${questionId}/${version}`,
        method: "GET",
        params: {
          pageNumber: data.pageNumber,
          pageSize: data.pageSize,
          sortOrder: data.sortOrder,
          sortKey: data.sortKey,
          fetchBy: data.fetchBy
        }
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    addSubTestCases: builder.mutation({
      query: (data: any) => ({
        url: `/testcase/subTestcases`,
        method: "POST",
        body: Array.isArray(data) ? [...data] : [data]
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    getFetchTestcase: builder.query({
      query: ({ testcaseId }) => ({
        url: `/testcase/fetchTestcase?testcaseId=${testcaseId}`,
        method: "GET"
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    deleteTestCase: builder.mutation({
      query: ({ version, questionId, testCaseId }: any) => ({
        url: `/testcase/${questionId}/${version}/${testCaseId}`,
        method: "POST"
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    updateCodingQuestionStatus: builder.mutation({
      query: ({ questionId, version, data }: any) => ({
        url: `/codingQn/status/${questionId}/${version}`,
        method: "PUT",
        body: data
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    mcqNewVersion: builder.mutation({
      query: ({ version, questionId }: any) => ({
        url: `/mcq/version/${questionId}/${version}`,
        method: "POST"
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    codingNewVersion: builder.mutation({
      query: ({ version, questionId }: any) => ({
        url: `/codingQn/version/${questionId}/${version}`,
        method: "POST"
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    codingClone: builder.mutation({
      query: ({ version, questionId }: any) => ({
        url: `/codingQn/clone/${questionId}/${version}`,
        method: "POST"
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    mcqClone: builder.mutation({
      query: ({ version, questionId }: any) => ({
        url: `/mcq/clone/${questionId}/${version}`,
        method: "POST"
      }),
      extraOptions: { service: "MCQ_Assessment" }
    }),
    questionsBulkActions: builder.mutation({
      query: (data) => ({
        url: `/group/quesBulkStatus/update`,
        method: "POST",
        body: data
      }),
      extraOptions: { service: "Resource" }
    }),
    getBulkQuestionDetails: builder.query({
      query: (questions: BulkQuestionInput[]) => ({
        url: "/graphql",
        body: gql`
          query GetBulkQuestionDetails($questions: [BulkQuestionInput!]!) {
            getBulkQuestionDetails(questions: $questions) {
              timestamp
              data {
                questionId
                version
                title
                problemStatement
                maxMarks
                difficultyLevel
                duration
                status
                skills
                practiceQuestion
                active
              }
              error
              version
            }
          }
        `,
        params: {
          variables: {
            questions
          }
        }
      }),
      extraOptions: { service: "MCQ_Assessment" }
    })
  })
});

export const {
  useGetMcqAssessmentApiQuery,
  useLazyGetMcqAssessmentApiQuery,
  useAddMcqSubtypeAssessmentApiMutation,
  useUpdateMcqAssessmentApiMutation,
  useUpdateMcqStatusApiMutation,
  useUpdateQuestionReviewRequiredApiMutation,
  useAddCodingQnMutation,
  useUpdateCodingQnMutation,
  useAddTestCasesMutation,
  useLazyGetTestCasesQuery,
  useGetTestCasesQuery,
  useAddSubTestCasesMutation,
  useLazyGetFetchTestcaseQuery,
  useGetFetchTestcaseQuery,
  useDeleteTestCaseMutation,
  useMcqNewVersionMutation,
  useCodingNewVersionMutation,
  useUpdateCodingQuestionStatusMutation,
  useCodingCloneMutation,
  useMcqCloneMutation,
  useQuestionsBulkActionsMutation,
  useGetBulkQuestionDetailsQuery,
  useLazyGetBulkQuestionDetailsQuery
} = McqAssessmentSliceApi;
